import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Field, TextInput, CopperButton } from "@/components/common/UI";
import { Loader2 } from "lucide-react";
import TID from "@/constants/testIds";

const QUICK_LOGINS = [
    { role: "CORPORATE_ADMIN", label: "Corporate Admin", email: "demo@testemail.com", password: "Admin@123", name: "Demo User" },
];

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e?.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email.trim().toLowerCase(), password);
        setSubmitting(false);
        if (res.ok) navigate("/");
        else setError(res.error);
    };

    const quickFill = (q) => {
        setEmail(q.email);
        setPassword(q.password);
        setError("");
    };

    return (
        <div className="min-h-screen flex bg-background">
            {/* Left — brand panel */}
            <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-slate-900">
                <div className="absolute inset-0 opacity-[0.07]" style={{
                    backgroundImage: "radial-gradient(circle at 30% 30%, hsl(18, 65%, 47%) 0%, transparent 50%), radial-gradient(circle at 80% 70%, hsl(18, 65%, 60%) 0%, transparent 40%)"
                }} />
                <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }} />

                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <div>
                        <div className="flex items-center gap-3 mb-12">
                            <div className="h-11 w-11 rounded-xl bg-[hsl(var(--copper))] flex items-center justify-center font-display font-bold text-xl">C</div>
                            <div>
                                <div className="font-display text-xl font-semibold">CostIQ</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Manufacturing Cost Intelligence</div>
                            </div>
                        </div>

                        <div className="max-w-md">
                            <div className="eyebrow text-[hsl(var(--copper))] mb-3">ABC COMPANY · PUNE</div>
                            <h1 className="font-display text-4xl font-semibold leading-tight mb-4">
                                One source of truth for every part you cost, price, and settle.
                            </h1>
                            <p className="text-slate-400 text-sm leading-relaxed">
                                Zero-Based Costing · RFQ Pricing · Quarterly Settlement.
                                From shop-floor weight to OEM negotiation — one disciplined system, one audit trail.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 max-w-md">
                        {[
                            { v: "12", l: "Plants" },
                            { v: "28", l: "Steel Grades" },
                            { v: "10", l: "OEM Customers" },
                        ].map((s) => (
                            <div key={s.l} className="border-l-2 border-[hsl(var(--copper))] pl-3">
                                <div className="font-mono font-semibold text-2xl tabular-nums">{s.v}</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right — login form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
                <div className="w-full max-w-[420px]">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--copper))] flex items-center justify-center text-white font-display font-bold">C</div>
                        <div>
                            <div className="font-display text-lg font-semibold">CostIQ</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Cost Intelligence</div>
                        </div>
                    </div>

                    <div className="mb-7">
                        <h2 className="font-display text-2xl font-semibold text-slate-900">Welcome back</h2>
                        <p className="text-sm text-slate-500 mt-1.5">Sign in to continue to CostIQ.</p>
                    </div>

                    <form onSubmit={submit} className="space-y-5">
                        <Field label="Email Address" required>
                            <TextInput
                                type="email"
                                autoComplete="email"
                                placeholder="you@testemail.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid={TID.loginEmail}
                            />
                        </Field>

                        <Field label="Password" required>
                            <TextInput
                                type="password"
                                autoComplete="current-password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid={TID.loginPassword}
                            />
                        </Field>

                        {error && (
                            <div
                                data-testid={TID.loginError}
                                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
                            >
                                {error}
                            </div>
                        )}

                        <CopperButton
                            type="submit"
                            disabled={submitting}
                            className="w-full h-10"
                            data-testid={TID.loginSubmit}
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {submitting ? "Signing in" : "Sign in"}
                        </CopperButton>
                    </form>

                    <div className="mt-8">
                        <div className="eyebrow text-slate-400 mb-3">DEMO ACCOUNTS · ONE-CLICK FILL</div>
                        <div className="grid grid-cols-2 gap-2">
                            {QUICK_LOGINS.map((q) => (
                                <button
                                    key={q.role}
                                    onClick={() => quickFill(q)}
                                    data-testid={TID.loginQuickFill(q.role)}
                                    className="text-left p-2.5 rounded-md border border-slate-200 bg-white hover:bg-[hsl(var(--copper-soft))] hover:border-[hsl(var(--copper))]/30 transition-colors"
                                >
                                    <div className="text-[11px] font-semibold text-slate-900">{q.label}</div>
                                    <div className="text-[10px] text-slate-500 truncate">{q.name}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 text-[11px] text-slate-400 text-center">
                        Powered by Kairoo Technologies · CostIQ v1.0
                    </div>
                </div>
            </div>
        </div>
    );
}
